import React, { memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useLocation } from 'react-router-dom';
import { useRouteAuth } from '../../hooks/routeAuth';
import { useAuthRedirect } from '../../hooks/routeAuth/useAuthRedirect';

import { ErrorBoundary } from './ErrorBoundary';
import { ProtectedRouteProps } from './types';
import { api } from '../../helpers/api/api';
import ClientsContext from '../../context/ClientsContext';

const ProtectedRouteComponent = ({ component: Component, roles, path, children, ...rest }: ProtectedRouteProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { clients } = useContext(ClientsContext);
  const user = api.getLoggedInUser();
  const { redirectToLogin, redirectToClientNotFound, redirectToHome } = useAuthRedirect();
  const validateAuth = useRouteAuth({
    pathname: location.pathname,
    matchPath: path,
    user,
    clients,
    roles,
  });

  const { isAuthenticated, hasValidClient, hasRequiredRole } = validateAuth();

  if (!isAuthenticated) {
    redirectToLogin(location);
    return null;
  }

  if (!hasValidClient) {
    redirectToClientNotFound();
    return null;
  }

  if (!hasRequiredRole) {
    redirectToHome();
    return null;
  }

  return (
    <Route
      {...rest}
      path={path}
      render={props => (
        <ErrorBoundary fallback={<div>{t('Something went wrong')}</div>}>
          <Component {...props}>{children}</Component>
        </ErrorBoundary>
      )}
    />
  );
};

export const ProtectedRoute = memo(ProtectedRouteComponent);
ProtectedRoute.displayName = 'ProtectedRoute';
